import { useEffect, useRef, useState } from 'react';
import { searchProjects, type ProjectSearchResult } from '@/api/planning';

interface ProjectPickerProps {
  selected: ProjectSearchResult | null;
  onSelect: (p: ProjectSearchResult | null) => void;
  autoFocus?: boolean;
}

const DEBOUNCE_MS = 300;
const MIN_CHARS = 3;

/** Compact debounced project search for the PO-edit add modals. */
export function ProjectPicker({ selected, onSelect, autoFocus }: ProjectPickerProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProjectSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const trimmed = query.trim();
    if (trimmed.length < MIN_CHARS) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await searchProjects(trimmed);
        setResults(res.results);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query]);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-td-blue/40 bg-td-blue/5 px-3 py-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-td-navy truncate">{selected.projectName || selected.name}</div>
          {selected.sale_order_name && (
            <div className="text-xs text-td-muted">{selected.sale_order_name}</div>
          )}
        </div>
        <button
          type="button"
          onClick={() => { onSelect(null); setQuery(''); }}
          className="shrink-0 text-xs font-semibold text-td-muted hover:text-red-600"
        >
          Wijzigen
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        autoFocus={autoFocus}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Zoek project op naam of S-nummer…"
        aria-label="Project zoeken"
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-td-navy focus:outline-none focus:ring-2 focus:ring-td-blue"
      />
      {loading && <span className="absolute right-3 top-2.5 text-xs text-gray-400">…</span>}
      {open && (
        <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-td-muted text-center">Geen projecten gevonden</div>
          ) : (
            results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => { onSelect(r); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-td-blue/5"
              >
                <div className="text-sm font-medium text-td-navy truncate">{r.projectName || r.name}</div>
                <div className="text-xs text-td-muted truncate">
                  {r.sale_order_name ? `${r.sale_order_name} · ` : ''}{r.address}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
